

import { useFields } from "@/providers/FieldsProvider";
import { useGuestLogs } from "@/providers/GuestLogs";
import { parseToLocalDate, parseDateString } from "@/lib/utils/parser";
import { useEventTimezone } from "@/providers/EventProvider";
import { GuestLogType } from "@/types/enums/GuestLogType";
import { GuestLog } from "@/types/GuestLog";
import { printedSeatOf, seatLabelText, seatPlaceText } from "@/lib/seating/seatNumber";
import { useTranslations } from "next-intl";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { useEffect, useRef, useState } from "react";

/**
 * A guest-log value is "blank" when the backend had nothing to write: `null`
 * (a cleared field), `undefined` (an old row that predates the column) or the
 * empty string. All three used to reach the rendered sentence verbatim.
 */
const blank = (v: string | null | undefined) =>
  v === null || v === undefined || v === "";

/** How every bulk lane spells a custom field in `guest_logs`. */
const ADDITIONAL_FIELD_PREFIX = "additionalFields.";

// `newValue` of a TICKET_WALLET_CLICKED row. Product names — not translated.
const WALLET_NAMES: Record<string, string> = {
  apple: "Apple Wallet",
  google: "Google Wallet",
};

export const useDocumentation = (entry: GuestLog) => {
  const t = useTranslations("guestDetails");
  const tErrors = useTranslations("errors");
  const t2 = useTranslations("guest-fields");
  // `seats.seatNumber` already exists in all 57 locales — the seating plan and
  // the guest's seat tab render the same label from it.
  const tSeating = useTranslations("seating");
  const tSms = useTranslations("smsGuest");
  const { updateNote } = useGuestLogs();
  const { allFields } = useFields();
  const eventTz = useEventTimezone();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [visibleNote, setVisibleNote] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [text, setText] = useState<string>(() => {
    // `companionId` carries the companion's NAME, not an id — it is stamped
    // client-side by `useMergedGuestTimeline` when a companion's row is folded
    // into its host's timeline. Blank on every row the host owns.
    const companionName = entry.companionId
      ? entry.companionId === "DELETED"
        ? t("unknown")
        : entry.companionId
      : null;

    // ONE sentence builder for both, with the companion's name in front.
    //
    // The companion arm used to build its own sentence, and both halves of it
    // were wrong: it resolved field labels with `t` (namespace `guestDetails`,
    // which carries 2 of the 73 field keys) instead of `t2` (`guest-fields`),
    // so a status change printed the raw column name `state`; and every
    // non-UPDATE type fell through a `return t(type) + ": " + name` that threw
    // `entry.field` away — which on a COMPANION_REMOVED row is the name of the
    // person who was REMOVED, so the row named the wrong human. The branch was
    // dead code until the merge fed it, and it drifted precisely because it was
    // a second copy.
    //
    // ⚠ The prefixed string is display-only, and safe ONLY because companion
    // rows are read-only (`Documentation.tsx` gates the pencil on
    // `!entry.companionId`). `textDbRef` keeps the RAW field.
    const withName = (body: string) =>
      companionName ? `${companionName}: ${body}` : body;

    if (entry.type === GuestLogType.NOTE) return withName(entry.field);

    // A field-level change sentence: "Status: WAITLISTED to ACCEPTED changed".
    // Custom fields have no catalogue key, so their raw name is the label.
    //
    // `from` defaults to the entry's own oldValue; PROMOTED overrides it,
    // because a row whose origin was never set still carries a destination
    // worth showing and an empty gap would read as a rendering bug.
    const changeSentence = (from: string = entry.oldValue ?? "") => {
      // A custom field arrives under EITHER spelling: the per-guest update diff
      // writes the bare key ("tier"), every bulk lane writes the Mongo path
      // ("additionalFields.tier") — deliberately, because field-scope redaction
      // classifies a bare name by asking whether it is a canonical column, and
      // a custom field named like one would otherwise be masked as that column.
      //
      // Only the bare spelling was read here, so every bulk edit of a custom
      // field rendered its dotted path through the catalogue and came out as a
      // missing-translation artifact instead of the field's own name.
      const rawField = entry.field ?? "";
      const bareField = rawField.startsWith(ADDITIONAL_FIELD_PREFIX)
        ? rawField.slice(ADDITIONAL_FIELD_PREFIX.length)
        : rawField;
      const definition = allFields.find((f) => f.name === bareField);
      const isCustomField =
        rawField.startsWith(ADDITIONAL_FIELD_PREFIX) || !!definition?.additionalField;
      // A custom field's label is the one its creator gave it — the bare key is
      // the identifier (charset-normalized), which is what the sentence used to
      // print: "tier_2: … geändert" for a field the whole cockpit calls
      // "Ticket tier". The bare name stays as the fallback for a definition
      // this event no longer has.
      const fieldLabel = isCustomField
        ? definition?.translation || bareField
        : t2(bareField);
      const to = entry.newValue;

      // Every `$unset` in the product leaves the SAME trace: `logClearedFields`
      // writes an UPDATE row with BOTH values null (the check-in reset —
      // `processBatchReset` — among them). Fed to the sentence unchanged that
      // read "Check-In-Status:  zu undefined geändert": an empty origin, the
      // literal word "undefined" for a destination that does not exist, and no
      // hint that the field was cleared at all. A cleared field has no
      // "from → to" to tell; the field name plus "cleared" is the whole story.
      if (blank(from) && blank(to)) return fieldLabel + ": " + t("cleared");

      return (
        fieldLabel +
        ": " +
        // A half-blank side is a real state ("was empty" / "is now empty"), not
        // a missing value — printing it raw yielded an invisible gap on the
        // left and "undefined" on the right.
        (blank(from) ? t("emptyValue") : from) +
        " " +
        t("to") +
        " " +
        (blank(to) ? t("emptyValue") : to) +
        " " +
        t("changed")
      );
    };

    if (entry.type === GuestLogType.UPDATE) {
      return withName(changeSentence());
    }

    // AUDIT 2026-08-14. An automatic waitlist promotion now writes real guest
    // FIELD names — `state` and `acceptanceState` — and it writes two rows.
    // Neither reached the UPDATE arm, so both fell through to the raw-field
    // return below and printed "state" / "acceptanceState" beside a chip that
    // already says "Promoted", discarding old/new entirely.
    //
    // ⚠ THREE shapes, not two. `SlottingWaitlistService` has always written
    // `field: "PROMOTED"` with EMPTY values — feeding that to the sentence
    // would produce "PROMOTED:  to  changed". So: only build the sentence when
    // there is an actual field change to describe.
    // Each clause below is load-bearing on its own, and each is pinned by its
    // own case in the sibling test — the audit found all three freely deletable
    // in the first version.
    if (entry.type === GuestLogType.PROMOTED) {
      const describesAFieldChange =
        // A row with no field names nothing to describe.
        !!entry.field &&
        // The slotting writer puts the literal type name in `field`. Feeding
        // that to the sentence yields "PROMOTED: … to … changed" — a sentence
        // about a column that does not exist.
        entry.field !== GuestLogType.PROMOTED &&
        // The DESTINATION is what a promotion row is for. Without it there is
        // nothing to say beyond the chip.
        !!entry.newValue;
      // Empty, not the type label. `Documentation.tsx` already renders
      // `t(entry.type)` as the chip beside this text, so returning it here made
      // the row read "[Promoted] 14.08.2026 (10:00) … Promoted". The old
      // behaviour printed the raw word "PROMOTED" instead — one wart traded for
      // another. The chip carries the whole meaning; the body has nothing to
      // add when there is no field change to describe.
      // Empty stays empty even for a companion: the chip already carries the
      // whole meaning, and a bare "Max Mustermann: " would be worse than blank.
      if (!describesAFieldChange) return "";
      // ⚠ An EMPTY origin is tolerated, not rejected. Requiring `oldValue` here
      // threw away a real destination: a guest whose `acceptanceState` was
      // never set before promotion produced `oldValue: ""`, and the row
      // collapsed to the bare word "Promoted" — discarding exactly the
      // information the two-row split was introduced to show.
      return withName(changeSentence(entry.oldValue || t("unknown")));
    }

    // Seating rows name a seat, and every other surface in the product names
    // it "Platz 1". This one printed the backend's raw `field` — which read
    // "RT 5 - Seat 0": English in every locale, and off by one against the seat
    // the guest is actually sitting on.
    //
    // The backend now splits the two halves (`field` = furniture name,
    // `newValue` = zero-based index) so the number can be localized here.
    // Rows written BEFORE that split carry the whole sentence in `field` and no
    // numeric `newValue`, so they fall through to the raw branch below and keep
    // reading exactly as they always did. No migration, no rewritten history.
    if (
      entry.type === GuestLogType.SEATED ||
      entry.type === GuestLogType.UNSEATED ||
      entry.type === GuestLogType.SEATING_CHECK_IN
    ) {
      const seatIndex = Number(entry.newValue);
      if (entry.newValue !== undefined && entry.newValue !== "" && Number.isInteger(seatIndex)) {
        // The PRINTED seat the row recorded (`seatNumber`/`seatRow`, written
        // when the chair carried a numbering — seats.io slice 1), else the
        // index made 1-based, exactly as every older row reads.
        const seatLabel = seatLabelText(
          tSeating,
          printedSeatOf({ seatIndex, seatNumber: entry.seatNumber, seatRow: entry.seatRow }),
        );
        // The piece's block/area the row recorded (seats.io Teil 2, Zug 3) in
        // front of the table: "Parkett · Block C · RT 5 · Seat 3". A row
        // without them reads exactly as before.
        return withName(seatPlaceText(entry, entry.field ? `${entry.field} · ${seatLabel}` : seatLabel));
      }
    }

    // A check-in / check-out row names the column it changed — `checkedIn` —
    // and nothing else. That is not a description: it fell through to the raw
    // `entry.field` return below and printed the literal string "checkedIn"
    // beside a chip that already reads "Eingecheckt". Same stance as PROMOTED
    // above: the chip carries the whole meaning, so the body stays empty rather
    // than repeating it in English. Any other `field` on these types (there is
    // none today) still falls through and is shown.
    if (
      (entry.type === GuestLogType.CHECK_IN ||
        entry.type === GuestLogType.CHECK_OUT) &&
      entry.field === "checkedIn"
    ) {
      return "";
    }

    // Ticket-portal rows carry their detail in `newValue`, with no `field`:
    // the wallet that was tapped, or the name of the file that was handed out.
    // Without this arm they fell to the type label below and repeated the chip.
    if (entry.type === GuestLogType.TICKET_WALLET_CLICKED && entry.newValue) {
      return withName(Object.hasOwn(WALLET_NAMES, entry.newValue) ? WALLET_NAMES[entry.newValue] : entry.newValue);
    }
    if (entry.type === GuestLogType.PORTAL_DOWNLOADED && !entry.field && entry.newValue) {
      return withName(entry.newValue);
    }
    // A lost workflow trigger (main-backend GuestLogType, 2026-09-23): `field`
    // is the literal "trigger" — a marker (no replay job exists yet), not a
    // description — and `newValue` is the trigger's NAME. Without this arm the
    // `entry.field` return below printed "trigger" beside the chip.
    if (entry.type === GuestLogType.WORKFLOW_TRIGGER_LOST) {
      return withName(entry.newValue || t(entry.type));
    }
    // SMS channel wave: an SMS row carries ids and a part count in `details`
    // (`sentSmsId`, `parts`, `workflowId`), never a sentence — the chip ("SMS
    // sent") says what happened, the body adds the billed parts when known;
    // the rest lives in the guest's SMS history. Never the raw `field`.
    if (entry.type === GuestLogType.SMS_SENT) {
      const parts = entry.details?.parts;
      return typeof parts === "number" && Number.isFinite(parts) && parts > 0
        ? withName(tSms("parts", { count: parts }))
        : "";
    }

    // Types that carry field info as a description. On a COMPANION_REMOVED /
    // DELETE row that `field` is the removed person's NAME — which is exactly
    // what the old companion arm discarded.
    if (entry.field) {
      return withName(entry.field);
    }

    return withName(t(entry.type));
  });

  const textDbRef = useRef<string>(
    entry.type === GuestLogType.NOTE ? entry.field : ""
  );

  useEffect(() => {
    // Gated on `companionId` as well: a merged companion row's display text
    // carries the name prefix while `textDbRef` holds the raw field, so the
    // comparison was permanently true. Inert today because the row is
    // read-only — but it meant anyone loosening that gate would inherit a
    // Save button that is already armed with the prefixed string.
    if (entry.type === GuestLogType.NOTE && !entry.companionId)
      setHasChanges(text !== textDbRef.current);
  }, [text, entry.type, entry.companionId]);

  // Function to get status badge color
  const getStatusColor = (
    type: GuestLogType,
    companionId: string | undefined
  ) => {
    if (companionId) return "chip-pink-bg chip-pink-fg";

    switch (type) {
      case GuestLogType.CREATE:
      case GuestLogType.PROMOTED:
      case GuestLogType.IMPORTED:
      case GuestLogType.CLONED:
      case GuestLogType.COMPANION_ADDED:
      case GuestLogType.REQUEST_APPROVED:
        return "surface-success chip-green-fg";

      case GuestLogType.UPDATE:
      case GuestLogType.CONTACTED:
        return "surface-blue chip-blue-fg";

      case GuestLogType.DELETE:
      case GuestLogType.DELETE_COMPANION:
      case GuestLogType.COMPANION_REMOVED:
      case GuestLogType.REQUEST_DENIED:
        return "surface-error chip-red-fg";

      case GuestLogType.CHECK_IN:
      case GuestLogType.SEATING_CHECK_IN:
      case GuestLogType.APPOINTMENT_CHECK_IN:
        return "chip-yellow-bg chip-yellow-fg";

      case GuestLogType.CHECK_OUT:
        return "surface-blue chip-blue-fg";

      case GuestLogType.NOTE:
      case GuestLogType.NOTE_EDIT:
        return "chip-purple-bg chip-purple-fg";

      case GuestLogType.APPOINTMENT_CREATED:
      case GuestLogType.APPOINTMENT_CONFIRMED:
      case GuestLogType.SEATED:
      case GuestLogType.WAITLIST_JOINED:
        return "surface-success chip-green-fg";

      case GuestLogType.APPOINTMENT_UPDATED:
      case GuestLogType.APPOINTMENT_RESCHEDULED:
        return "surface-blue chip-blue-fg";

      case GuestLogType.APPOINTMENT_CANCELLED:
      case GuestLogType.APPOINTMENT_DECLINED:
      case GuestLogType.UNSEATED:
      case GuestLogType.WAITLIST_CANCELLED:
      case GuestLogType.WAITLISTED:
      case GuestLogType.ARCHIVED:
      case GuestLogType.UNARCHIVED:
      case GuestLogType.WORKFLOW_TRIGGER_LOST:
        return "surface-orange chip-orange-fg";

      case GuestLogType.EMAIL_SENT:
      case GuestLogType.SMS_SENT:
      case GuestLogType.REQUEST_SUBMITTED:
        return "surface-blue chip-blue-fg";

      case GuestLogType.INVITATION_USED:
        return "surface-success chip-green-fg";

      case GuestLogType.PORTAL_VISITED:
      case GuestLogType.PORTAL_DOWNLOADED:
      case GuestLogType.TICKET_PORTAL_VISITED:
      case GuestLogType.TICKET_WALLET_CLICKED:
      case GuestLogType.WEBSITE_LOGIN:
      case GuestLogType.QR_LOGIN:
      case GuestLogType.EXPORTED:
        return "chip-purple-bg chip-purple-fg";

      default:
        return "surface-blue chip-blue-fg";
    }
  };

  // Function to format date in European format (DD.MM.YYYY)
  const formatDate = (date: Date | string): string => {
    // `parseToLocalDate` returns UNDEFINED for anything it cannot parse, and
    // the non-null assertion used to hand that straight to `.getTime()` — a
    // TypeError thrown during render, with no local ErrorBoundary, so one bad
    // row took the whole app to the Sentry boundary. `useMergedGuestTimeline`
    // even documents tolerance for exactly this input.
    const dateObj = typeof date === "string" ? parseToLocalDate(date) : date;

    if (!dateObj || isNaN(dateObj.getTime())) {
      return "Invalid date";
    }

    // Render in the EVENT tz so the log date agrees with every other event-tz
    // surface (was browser-local via getDate/getMonth — off by a day for an
    // admin viewing across the date line from the event).
    return parseDateString(dateObj, "de-DE", eventTz);
  };

  // Function to format time in European format (24-hour clock: HH:MM)
  const formatTime = (date: Date | string): string => {
    // Same crash path as formatDate — see there.
    const dateObj = typeof date === "string" ? parseToLocalDate(date) : date;

    if (!dateObj || isNaN(dateObj.getTime())) {
      return "Invalid date";
    }

    // Event-tz time (was browser-local via getHours/getMinutes).
    return new Intl.DateTimeFormat("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
      ...(eventTz ? { timeZone: eventTz } : {}),
    }).format(dateObj);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await updateNote(entry.guestId!, entry.guestLogId!, text);
    } catch (e) {
      // Stay in edit mode and say so. Closing the editor on a rejected save —
      // with the new text still on screen and `textDbRef` advanced — is
      // indistinguishable from success; only a reload revealed the old value.
      //
      // Translated from the backend CODE: `updateNote` throws
      // `validationError` for a blank note before any request, and
      // `noAccessToResource` for a row-scope refusal. Collapsing both into
      // "please try again" makes the first look like an outage and the second
      // look retryable.
      setSaveError(
        translateBackendError(tErrors, e as Error, tErrors("unableToSave")),
      );
      setIsLoading(false);
      return;
    }
    textDbRef.current = text;
    setSaveError(null);
    setEditMode(false);
    setIsLoading(false);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (editMode) {
      setText(e.target.value);
      setSaveError(null);
    }
  };

  const handleEnterHover = () => {
    setVisibleNote(true);
  };

  const handleLeaveHover = () => {
    setVisibleNote(false);
  };

  const handleEnableEditMode = () => {
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setSaveError(null);
    setText(textDbRef.current);
    setEditMode(false);
  };

  const handleResetText = () => {
    setSaveError(null);
    setText(textDbRef.current);
  };

  return {
    editMode,
    saveError,
    visibleNote,
    hasChanges,
    isLoading,
    text,
    getStatusColor,
    formatDate,
    formatTime,
    handleSave,
    handleTextChange,
    handleEnterHover,
    handleLeaveHover,
    handleEnableEditMode,
    handleCancelEdit,
    handleResetText,
    t,
  };
};
